import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ArrowLeft, Sparkles, Loader2, Building2, Users, Zap, Wifi,
  Mic, Square, ArrowRight, MessageSquare, Monitor, Brain, Shield,
  CheckCircle2, TrendingUp, Clock, Lightbulb, ChevronRight,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';



const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Question data ── */
const questions = [
  {
    key: 'branche', icon: Building2,
    title: 'In welcher Branche sind Sie tätig?',
    subtitle: 'So können wir branchenspezifische KI-Lösungen identifizieren.',
    options: ['IT & Software', 'Handel & E-Commerce', 'Produktion & Industrie', 'Dienstleistung & Beratung', 'Gesundheit & Pharma', 'Finanzen & Versicherung', 'Andere'],
  },
  {
    key: 'unternehmengroesse', icon: Users,
    title: 'Wie groß ist Ihr Unternehmen?',
    subtitle: 'Wir skalieren unsere Empfehlungen passend zu Ihrer Teamgröße.',
    options: ['1–10', '11–50', '51–200', '201–1.000', 'Über 1.000'],
  },
  {
    key: 'herausforderung', icon: Zap,
    title: 'Was ist Ihre größte Herausforderung?',
    subtitle: 'Hier setzen wir den Hebel für maximalen Impact.',
    options: ['Repetitive manuelle Aufgaben', 'Datenanalyse & Reporting', 'Kundenakquise & Vertrieb', 'Prozessoptimierung', 'Kommunikation & Zusammenarbeit'],
  },
  {
    key: 'digitalisierungsgrad', icon: Wifi,
    title: 'Wie digital ist Ihr Unternehmen?',
    subtitle: 'Dies bestimmt den optimalen Einstiegspunkt.',
    options: ['Kaum digital', 'Teilweise digitalisiert', 'Gut digitalisiert', 'Voll digitalisiert'],
  },
];

/* ── Insights shown between steps ── */
const insightData = {
  branche: {
    'IT & Software': 'IT-Unternehmen erzielen mit KI-gestütztem Code Review und automatisierter CI/CD-Pipeline bis zu 40% schnellere Release-Zyklen.',
    'Handel & E-Commerce': 'KI-Personalisierung steigert den durchschnittlichen Warenkorbwert im E-Commerce um 15–25%.',
    'Produktion & Industrie': 'Predictive Maintenance mit KI reduziert ungeplante Ausfallzeiten um bis zu 50%.',
    'Dienstleistung & Beratung': 'KI-Assistenten automatisieren in Beratungsfirmen bis zu 35% der Dokumentation und Analyse.',
    'Gesundheit & Pharma': 'KI-gestützte Diagnoseunterstützung verbessert die Trefferquote in der Befundung um 20–30%.',
    'Finanzen & Versicherung': 'Automatisierte Risikobewertung mit KI verkürzt Bearbeitungszeiten um durchschnittlich 60%.',
    'Andere': 'Branchenübergreifend spart KI-Automatisierung im Schnitt 25% bei repetitiven Verwaltungsaufgaben.',
  },
  unternehmengroesse: {
    '1–10': 'Für kleine Teams kann ein einzelner KI-Agent die Arbeit von 2–3 zusätzlichen Mitarbeitern übernehmen.',
    '11–50': 'In dieser Unternehmensgröße liegt das größte KI-Potenzial in der Automatisierung interner Workflows.',
    '51–200': 'Unternehmen Ihrer Größe profitieren am meisten von KI-gestützter Datenanalyse und Prozessautomatisierung.',
    '201–1.000': 'Bei Ihrer Unternehmensgröße multipliziert sich jede Effizienzsteigerung durch KI erheblich.',
    'Über 1.000': 'Enterprise-KI-Lösungen können bei Ihrer Größe Millioneneinsparungen pro Jahr generieren.',
  },
  herausforderung: {
    'Repetitive manuelle Aufgaben': 'Genau hier entfaltet KI die größte Wirkung — bis zu 80% dieser Aufgaben lassen sich automatisieren.',
    'Datenanalyse & Reporting': 'KI-Dashboards liefern in Echtzeit Insights, die manuell Tage dauern würden.',
    'Kundenakquise & Vertrieb': 'KI-Lead-Scoring erhöht die Conversion-Rate um durchschnittlich 30%.',
    'Prozessoptimierung': 'Process Mining mit KI deckt versteckte Ineffizienzen auf, die manuell kaum sichtbar sind.',
    'Kommunikation & Zusammenarbeit': 'KI-gestützte Tools wie automatische Meeting-Zusammenfassungen sparen Teams 5+ Stunden pro Woche.',
  },
  digitalisierungsgrad: {
    'Kaum digital': 'Perfekter Startpunkt — mit gezielten Quick Wins können Sie in 2 Wochen erste KI-Ergebnisse sehen.',
    'Teilweise digitalisiert': 'Ihre bestehende digitale Basis ist ideal, um KI nahtlos in vorhandene Prozesse zu integrieren.',
    'Gut digitalisiert': 'Exzellent — Sie können direkt mit fortgeschrittenen KI-Automatisierungen starten.',
    'Voll digitalisiert': 'Als digitaler Vorreiter sind Sie bereit für AI-Native Workflows und Predictive Intelligence.',
  },
};

/* ── Analyzing phases ── */
const analyzingPhases = [
  { icon: Brain, text: 'Analysiere Ihre Branche...' },
  { icon: Monitor, text: 'Prüfe Software-Integrationen...' },
  { icon: TrendingUp, text: 'Berechne Einsparpotenzial...' },
  { icon: Sparkles, text: 'Generiere personalisierte Empfehlungen...' },
];

/* ── Step identifiers ── */
const TOTAL_Q = questions.length;
const STEP_SOFTWARE = TOTAL_Q;
const STEP_FREITEXT = TOTAL_Q + 1;
const STEP_ANALYZING = TOTAL_Q + 2;
const STEP_RESULT = TOTAL_Q + 3;
const TOTAL_INPUT_STEPS = TOTAL_Q + 2; // questions + software + freitext

/* ── Motion variants ── */
const pageVariants = {
  enter: (dir) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
  exit: (dir) => ({ x: dir > 0 ? -80 : 80, opacity: 0, transition: { duration: 0.25 } }),
};

const insightVariants = {
  hidden: { opacity: 0, scale: 0.9, y: 20 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, scale: 0.95, y: -10, transition: { duration: 0.3 } },
};

/* ── MIME helper ── */
function getSupportedMimeType() {
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
  for (const t of types) { if (MediaRecorder.isTypeSupported(t)) return t; }
  return '';
}

/* ════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════ */
const KIAssessment = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(-1); // -1 = welcome
  const [answers, setAnswers] = useState({});
  const [software, setSoftware] = useState('');
  const [freitext, setFreitext] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [direction, setDirection] = useState(1);

  // Insight interstitial
  const [showInsight, setShowInsight] = useState(false);
  const [currentInsight, setCurrentInsight] = useState('');

  // Voice recording
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [activeVoiceField, setActiveVoiceField] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  // Analyzing phase
  const [analyzePhase, setAnalyzePhase] = useState(0);

  const handleReset = () => {
    setStep(-1);
    setAnswers({});
    setSoftware('');
    setFreitext('');
    setResult(null);
    setIsLoading(false);
    setDirection(1);
    setShowInsight(false);
    setAnalyzePhase(0);
  };

  const handleClose = () => {
    if (isRecording) stopRecording();
    handleReset();
    onClose();
  };

  /* ── Assessment submit ── */
  const submitAssessment = useCallback(async () => {
    setDirection(1);
    setStep(STEP_ANALYZING);
    setIsLoading(true);
    setAnalyzePhase(0);

    // Cycle through analyzing phases
    const phaseInterval = setInterval(() => {
      setAnalyzePhase((p) => (p + 1) % analyzingPhases.length);
    }, 2000);

    try {
      const payload = { ...answers, software: software || undefined, freitext: freitext || undefined };
      const res = await axios.post(`${BACKEND_URL}/api/assessment`, payload);
      clearInterval(phaseInterval);
      setResult(res.data.empfehlung);
      setStep(STEP_RESULT);
    } catch {
      clearInterval(phaseInterval);
      setResult('## Fehler\n\nBitte versuchen Sie es erneut oder kontaktieren Sie uns direkt.');
      setStep(STEP_RESULT);
    } finally {
      setIsLoading(false);
    }
  }, [answers, software, freitext]);

  /* ── Question selection with insight interstitial ── */
  const handleSelect = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    setDirection(1);

    // Get the insight for this answer
    const insight = insightData[key]?.[value];
    if (insight) {
      setCurrentInsight(insight);
      setShowInsight(true);
      setTimeout(() => {
        setShowInsight(false);
        setTimeout(() => {
          if (step < TOTAL_Q - 1) setStep((s) => s + 1);
          else setStep(STEP_SOFTWARE);
        }, 150);
      }, 2200);
    } else {
      setTimeout(() => {
        if (step < TOTAL_Q - 1) setStep((s) => s + 1);
        else setStep(STEP_SOFTWARE);
      }, 300);
    }
  };

  const handleBack = () => {
    setDirection(-1);
    if (step === STEP_FREITEXT) setStep(STEP_SOFTWARE);
    else if (step === STEP_SOFTWARE) setStep(TOTAL_Q - 1);
    else if (step > 0) setStep((s) => s - 1);
    else if (step === 0) setStep(-1);
  };

  /* ── Voice recording ── */
  const transcribeBlob = async (blob, targetField) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      const ext = blob.type.includes('webm') ? 'webm' : blob.type.includes('ogg') ? 'ogg' : 'mp4';
      formData.append('file', blob, `recording.${ext}`);
      const res = await axios.post(`${BACKEND_URL}/api/transcribe`, formData);
      if (res.data.text) {
        if (targetField === 'software') setSoftware((prev) => (prev ? `${prev}, ${res.data.text}` : res.data.text));
        else setFreitext((prev) => (prev ? `${prev} ${res.data.text}` : res.data.text));
      }
    } catch (err) { console.error('Transcription failed:', err); }
    finally { setIsTranscribing(false); }
  };

  const startRecording = async (targetField) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setActiveVoiceField(targetField);
      const mimeType = getSupportedMimeType();
      const options = mimeType ? { mimeType } : {};
      const mr = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
        if (chunksRef.current.length > 0) {
          const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
          transcribeBlob(blob, targetField);
        }
      };
      mr.start(1000);
      setIsRecording(true);
    } catch (err) { console.error('Mic error:', err); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    setIsRecording(false);
    setActiveVoiceField(null);
  };

  if (!isOpen) return null;

  const currentQuestion = step >= 0 && step < TOTAL_Q ? questions[step] : null;
  const progressPercent = step <= -1 ? 0 : Math.min(((step + 1) / TOTAL_INPUT_STEPS) * 100, 100);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] assessment-bg flex flex-col"
      data-testid="assessment-fullscreen"
    >
      {/* ── Header ── */}
      <header className="flex items-center justify-between px-6 py-5 relative z-10 flex-shrink-0">
        <div className="flex items-center gap-3">
          <img src="/logo-white.png" alt="LAIzyones" className="h-10 object-contain" />
        </div>

        {/* Step dots */}
        {step >= 0 && step < STEP_ANALYZING && (
          <div className="hidden sm:flex items-center gap-1.5">
            {Array.from({ length: TOTAL_INPUT_STEPS }).map((_, i) => (
              <div
                key={i}
                className={`step-dot ${i === step ? 'active' : i < step ? 'completed' : ''}`}
              />
            ))}
          </div>
        )}

        <button
          data-testid="assessment-close"
          onClick={handleClose}
          className="w-9 h-9 rounded-xl bg-white/5 border border-white/8 flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/10 transition-all"
        >
          <X className="w-4 h-4" />
        </button>
      </header>

      {/* ── Progress bar ── */}
      {step >= 0 && step < STEP_ANALYZING && (
        <div className="px-6 flex-shrink-0">
          <div className="h-0.5 bg-white/5 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full"
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
        </div>
      )}

      {/* ── Main content area ── */}
      <div className="flex-1 flex items-center justify-center overflow-y-auto px-4 py-8">
        <div className="w-full max-w-xl">
          <AnimatePresence mode="wait" custom={direction}>

            {/* ── WELCOME STEP ── */}
            {step === -1 && (
              <motion.div
                key="welcome"
                custom={direction}
                variants={pageVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="text-center"
                data-testid="assessment-welcome"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 15 }}
                  className="relative inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/20 mb-8"
                >
                  <Brain className="w-10 h-10 text-emerald-400" />
                  <div className="pulse-ring rounded-3xl" />
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                  className="font-['Space_Grotesk'] font-bold text-2xl md:text-3xl text-white mb-3"
                >
                  Entdecken Sie Ihr{' '}
                  <span className="text-shimmer">KI-Potenzial</span>
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="text-gray-400 text-base mb-3 max-w-md mx-auto leading-relaxed"
                >
                  In nur 60 Sekunden erstellt unsere KI eine personalisierte Analyse
                  mit konkreten Empfehlungen für Ihr Unternehmen.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="flex flex-wrap items-center justify-center gap-4 mb-10 text-xs text-gray-500"
                >
                  {[
                    { icon: Clock, text: '60 Sekunden' },
                    { icon: Shield, text: 'Keine Daten an Dritte' },
                    { icon: Sparkles, text: 'KI-powered' },
                  ].map(({ icon: Ic, text }) => (
                    <div key={text} className="flex items-center gap-1.5">
                      <Ic className="w-3.5 h-3.5 text-emerald-500/60" />
                      <span>{text}</span>
                    </div>
                  ))}
                </motion.div>

                <motion.button
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  data-testid="assessment-start"
                  onClick={() => { setDirection(1); setStep(0); }}
                  className="btn-primary text-base px-8 py-4"
                >
                  <span>Assessment starten</span>
                  <ArrowRight className="w-5 h-5 relative z-10" />
                </motion.button>
              </motion.div>
            )}

            {/* ── INSIGHT INTERSTITIAL ── */}
            {showInsight && step >= 0 && step < TOTAL_Q && (
              <motion.div
                key="insight"
                variants={insightVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="text-center py-8"
                data-testid="assessment-insight"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mb-6">
                  <Lightbulb className="w-7 h-7 text-emerald-400" />
                </div>
                <p className="text-gray-300 text-base leading-relaxed max-w-md mx-auto">
                  {currentInsight}
                </p>
              </motion.div>
            )}

            {/* ── QUESTION STEPS ── */}
            {!showInsight && step >= 0 && step < TOTAL_Q && currentQuestion && (
              <motion.div
                key={`q-${step}`}
                custom={direction}
                variants={pageVariants}
                initial="enter"
                animate="center"
                exit="exit"
                data-testid={`assessment-q-${step}`}
              >
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500/15 to-cyan-500/15 border border-emerald-500/15 flex items-center justify-center">
                    {currentQuestion.icon && <currentQuestion.icon className="w-6 h-6 text-emerald-400" />}
                  </div>
                  <div>
                    <h3 className="font-['Space_Grotesk'] font-bold text-xl text-white">
                      {currentQuestion.title}
                    </h3>
                    <p className="text-gray-500 text-sm mt-0.5">{currentQuestion.subtitle}</p>
                  </div>
                </div>

                <div className="space-y-2.5">
                  {currentQuestion.options.map((option) => {
                    const isSelected = answers[currentQuestion.key] === option;
                    return (
                      <button
                        key={option}
                        data-testid={`option-${option}`}
                        onClick={() => handleSelect(currentQuestion.key, option)}
                        className={`option-card w-full text-left flex items-center justify-between ${isSelected ? 'selected' : ''}`}
                      >
                        <span className="text-sm text-gray-300 relative z-10">{option}</span>
                        {isSelected && <CheckCircle2 className="w-4 h-4 text-emerald-400 relative z-10" />}
                      </button>
                    );
                  })}
                </div>

                {step > 0 && (
                  <button
                    data-testid="assessment-back"
                    onClick={handleBack}
                    className="mt-6 flex items-center gap-1.5 text-gray-600 text-sm hover:text-gray-300 transition-colors"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" /> Zurück
                  </button>
                )}
              </motion.div>
            )}

            {/* ── SOFTWARE STEP ── */}
            {step === STEP_SOFTWARE && (
              <motion.div
                key="software"
                custom={direction}
                variants={pageVariants}
                initial="enter"
                animate="center"
                exit="exit"
                data-testid="assessment-software"
              >
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500/15 to-cyan-500/15 border border-emerald-500/15 flex items-center justify-center">
                    <Monitor className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-['Space_Grotesk'] font-bold text-xl text-white">
                      Welche Software nutzen Sie?
                    </h3>
                    <p className="text-gray-500 text-sm mt-0.5">
                      Wir prüfen direkt, welche API-Integrationen möglich sind.
                    </p>
                  </div>
                </div>

                <div className="relative mb-4">
                  <input
                    data-testid="software-input"
                    value={software}
                    onChange={(e) => setSoftware(e.target.value)}
                    placeholder="z.B. SAP, Salesforce, HubSpot, Slack..."
                    className="w-full px-4 py-3.5 pr-14 bg-white/[0.03] border border-white/10 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-emerald-500/40 focus:shadow-[0_0_20px_rgba(16,185,129,0.08)] transition-all"
                  />
                  <VoiceButton
                    field="software"
                    isRecording={isRecording}
                    activeVoiceField={activeVoiceField}
                    isTranscribing={isTranscribing}
                    onStart={() => startRecording('software')}
                    onStop={stopRecording}
                  />
                </div>

                <RecordingIndicator
                  isRecording={isRecording}
                  isTranscribing={isTranscribing}
                  activeField={activeVoiceField}
                  field="software"
                />

                <div className="flex gap-3 mt-6">
                  <button
                    data-testid="assessment-back"
                    onClick={handleBack}
                    className="px-5 py-3 rounded-xl border border-white/10 text-gray-500 text-sm flex items-center gap-1.5 hover:text-white hover:border-white/20 transition-all"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" /> Zurück
                  </button>
                  <button
                    data-testid="software-next"
                    onClick={() => { setDirection(1); setStep(STEP_FREITEXT); }}
                    disabled={isTranscribing || isRecording}
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {software ? 'Weiter' : 'Überspringen'} <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* ── FREITEXT STEP ── */}
            {step === STEP_FREITEXT && (
              <motion.div
                key="freitext"
                custom={direction}
                variants={pageVariants}
                initial="enter"
                animate="center"
                exit="exit"
                data-testid="assessment-freitext"
              >
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500/15 to-cyan-500/15 border border-emerald-500/15 flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-['Space_Grotesk'] font-bold text-xl text-white">
                      Noch etwas hinzufügen?{' '}
                      <span className="text-gray-600 font-normal text-sm">(optional)</span>
                    </h3>
                    <p className="text-gray-500 text-sm mt-0.5">
                      Spezifische Wünsche oder Herausforderungen — per Text oder Sprache.
                    </p>
                  </div>
                </div>

                <div className="relative mb-4">
                  <textarea
                    data-testid="freitext-input"
                    value={freitext}
                    onChange={(e) => setFreitext(e.target.value)}
                    placeholder="z.B. Wir verlieren viel Zeit mit manueller Dateneingabe..."
                    rows={4}
                    className="w-full px-4 py-3.5 pr-14 bg-white/[0.03] border border-white/10 rounded-xl text-white text-sm placeholder-gray-600 resize-none focus:outline-none focus:border-emerald-500/40 focus:shadow-[0_0_20px_rgba(16,185,129,0.08)] transition-all"
                  />
                  <VoiceButton
                    field="freitext"
                    isRecording={isRecording}
                    activeVoiceField={activeVoiceField}
                    isTranscribing={isTranscribing}
                    onStart={() => startRecording('freitext')}
                    onStop={stopRecording}
                  />
                </div>

                <RecordingIndicator
                  isRecording={isRecording}
                  isTranscribing={isTranscribing}
                  activeField={activeVoiceField}
                  field="freitext"
                />

                <div className="flex gap-3 mt-6">
                  <button
                    data-testid="assessment-back"
                    onClick={handleBack}
                    className="px-5 py-3 rounded-xl border border-white/10 text-gray-500 text-sm flex items-center gap-1.5 hover:text-white hover:border-white/20 transition-all"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" /> Zurück
                  </button>
                  <button
                    data-testid="assessment-submit"
                    onClick={submitAssessment}
                    disabled={isTranscribing || isRecording}
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    KI-Analyse starten <Sparkles className="w-3.5 h-3.5" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* ── ANALYZING STEP ── */}
            {step === STEP_ANALYZING && (
              <motion.div
                key="analyzing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-8"
                data-testid="assessment-loading"
              >
                {/* Orbital animation */}
                <div className="relative inline-flex items-center justify-center w-24 h-24 mb-8">
                  <div className="absolute inset-0 rounded-full border border-emerald-500/20 analyzing-orbit">
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.6)]" />
                  </div>
                  <div className="absolute inset-3 rounded-full border border-cyan-500/15" style={{ animation: 'analyzing-orbit 5s linear infinite reverse' }}>
                    <div className="absolute -bottom-1 right-0 w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_6px_rgba(6,182,212,0.5)]" />
                  </div>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/15 to-cyan-500/15 border border-emerald-500/15 flex items-center justify-center">
                    <Brain className="w-7 h-7 text-emerald-400" />
                  </div>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={analyzePhase}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-col items-center gap-2"
                  >
                    <p className="text-white font-['Space_Grotesk'] font-semibold text-lg">
                      {analyzingPhases[analyzePhase].text}
                    </p>
                    <p className="text-gray-600 text-sm">
                      Unsere KI arbeitet an Ihrer persönlichen Analyse
                    </p>
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            )}

            {/* ── RESULT STEP ── */}
            {step === STEP_RESULT && result && (
              <motion.div
                key="result"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                data-testid="assessment-result"
                className="pb-8"
              >
                {/* Result header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-['Space_Grotesk'] font-bold text-lg text-white">Ihre Analyse</h3>
                    <p className="text-gray-600 text-xs">Personalisiert durch KI</p>
                  </div>
                </div>

                {/* Result content in a glass card */}
                <div className="glass-card-dark mb-6" style={{ padding: '1.5rem' }}>
                  <div className="prose prose-invert prose-emerald max-w-none
                    [&_h2]:text-xl [&_h2]:font-['Space_Grotesk'] [&_h2]:font-bold [&_h2]:text-white [&_h2]:mb-3 [&_h2]:mt-0
                    [&_h3]:text-base [&_h3]:font-['Space_Grotesk'] [&_h3]:font-semibold [&_h3]:text-emerald-400 [&_h3]:mt-5 [&_h3]:mb-2
                    [&_p]:text-gray-300 [&_p]:leading-relaxed [&_p]:mb-2 [&_p]:text-sm
                    [&_strong]:text-white [&_ol]:space-y-1.5 [&_ol]:text-gray-300 [&_ol]:pl-4 [&_ol]:text-sm [&_li]:text-gray-300 [&_li]:text-sm
                    [&_ul]:space-y-1 [&_ul]:pl-4 [&_ul]:text-sm
                  ">
                    <ResultTypewriter text={result} />
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex flex-col gap-2.5">
                  <button
                    data-testid="assessment-contact"
                    onClick={() => {
                      handleClose();
                      setTimeout(() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' }), 300);
                    }}
                    className="w-full py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity text-sm"
                  >
                    Kostenloses Beratungsgespräch <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    data-testid="assessment-retry"
                    onClick={handleReset}
                    className="w-full py-3 rounded-xl border border-white/10 text-gray-500 text-sm hover:text-white hover:border-white/20 transition-all"
                  >
                    Neues Assessment starten
                  </button>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

/* ── Voice button sub-component ── */
const VoiceButton = ({ field, isRecording, activeVoiceField, isTranscribing, onStart, onStop }) => {
  const isActive = isRecording && activeVoiceField === field;
  const isBusy = isTranscribing && activeVoiceField === field;
  const isDisabled = isTranscribing || (isRecording && activeVoiceField !== field);

  return (
    <button
      data-testid={`voice-btn-${field}`}
      onClick={isActive ? onStop : onStart}
      disabled={isDisabled}
      className={`absolute right-3 top-3 w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
        isActive
          ? 'bg-red-500/15 text-red-400 border border-red-500/30'
          : isBusy
            ? 'bg-white/5 text-gray-600'
            : 'bg-white/5 border border-white/8 text-gray-500 hover:text-emerald-400 hover:border-emerald-500/30 hover:bg-emerald-500/5'
      }`}
    >
      {isBusy ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : isActive ? (
        <Square className="w-3.5 h-3.5 fill-red-400" />
      ) : (
        <Mic className="w-4 h-4" />
      )}
    </button>
  );
};

/* ── Recording indicator ── */
const RecordingIndicator = ({ isRecording, isTranscribing, activeField, field }) => {
  if (isRecording && activeField === field) {
    return (
      <motion.p
        initial={{ opacity: 0, y: -5 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-red-400 text-xs flex items-center gap-1.5"
      >
        <span className="w-2 h-2 bg-red-400 rounded-full animate-pulse" /> Aufnahme läuft — Tippen zum Stoppen
      </motion.p>
    );
  }
  if (isTranscribing && activeField === field) {
    return (
      <motion.p
        initial={{ opacity: 0, y: -5 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-emerald-400 text-xs flex items-center gap-1.5"
      >
        <Loader2 className="w-3 h-3 animate-spin" /> Wird transkribiert...
      </motion.p>
    );
  }
  return null;
};

/* ── Typewriter result renderer ── */
const ResultTypewriter = ({ text }) => {
  const [displayed, setDisplayed] = useState('');
  const [isDone, setIsDone] = useState(false);

  useEffect(() => {
    if (!text) return;
    let i = 0;
    const speed = 8; // ms per character
    const timer = setInterval(() => {
      i += 2; // 2 chars at a time for speed
      if (i >= text.length) {
        setDisplayed(text);
        setIsDone(true);
        clearInterval(timer);
      } else {
        setDisplayed(text.slice(0, i));
      }
    }, speed);
    return () => clearInterval(timer);
  }, [text]);

  return (
    <div className={!isDone ? 'typewriter-cursor' : ''}>
      <ReactMarkdown>{displayed}</ReactMarkdown>
    </div>
  );
};

export default KIAssessment;
